import React, { useMemo, useState } from 'react';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from 'clsx';
import { StatusManager } from 'common/components/StatusManager';
import { AI_ENDPOINTS, CALL_VERBIAGES_TYPES } from 'features/ai-generators/constants';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import queryString from 'query-string';
import { SECONDARY_API_CLIENT } from 'services/API';

import { useStyles } from './CallVerbiages.styled';

export const CallVerbiages = ({ callType = CALL_VERBIAGES_TYPES.recruiting.id }) => {
  const classes = useStyles();
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [expandedItemId, setExpandedItemId] = useState(null);
  const callTypeConfig = CALL_VERBIAGES_TYPES[callType] || {};

  const { state } = useFetchWithStatusV2({
    apiInstance: SECONDARY_API_CLIENT,
    endpoint: `${AI_ENDPOINTS.roleplayCallVerbiages}?${queryString.stringify({
      callType: callTypeConfig.type
    })}`
  });

  const callVerbiageItems = state?.results || [];
  const topicGroups = useMemo(() => {
    const groups = new Map();

    callVerbiageItems.forEach(item => {
      const topic = item?.topic;
      if (!groups.has(topic)) groups.set(topic, []);
      groups.get(topic).push(item);
    });

    return Array.from(groups.entries()).map(([topic, items]) => ({
      topic,
      items
    }));
  }, [callVerbiageItems]);

  const handleTopicChange = topic => (_event, isExpanded) => {
    setExpandedTopic(isExpanded ? topic : null);
    if (!isExpanded) setExpandedItemId(null);
  };

  const handleItemChange = itemId => (_event, isExpanded) => {
    setExpandedItemId(isExpanded ? itemId : null);
  };

  return (
    <StatusManager
      status={state.status}
      slots={{
        Loading: (
          <Box alignItems="center" display="flex" height="50%" justifyContent="center">
            <CircularProgress />
          </Box>
        )
      }}
    >
      <Box display="flex" flexDirection="column" gridGap={16} mx={3} my={4}>
        <Typography variant="subtitle1">{callTypeConfig.title}</Typography>
        <Typography color="textSecondary" variant="body2">
          {callTypeConfig.description}
        </Typography>

        <div>
          {topicGroups.map(({ topic, items }) => {
            const isTopicExpanded = expandedTopic === topic;
            const hasMultipleItems = items.length > 1;
            return (
              <Accordion expanded={isTopicExpanded} key={topic} onChange={handleTopicChange(topic)}>
                <AccordionSummary
                  className={classes.accordionSummary}
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Box alignItems="center" display="flex" gridGap={8} width="100%">
                    <Typography variant={isTopicExpanded ? 'subtitle1' : 'body1'}>
                      {topic}
                    </Typography>

                    <Box className={classes.countChip}>
                      <Typography color="primary" variant="caption">
                        {items.length}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  className={clsx(classes.accordionDetails, hasMultipleItems && classes.noPadding)}
                >
                  {!hasMultipleItems ? (
                    <>
                      <Typography
                        component="div"
                        dangerouslySetInnerHTML={{ __html: items[0]?.verbiage || '' }}
                        variant="body2"
                      />
                    </>
                  ) : (
                    <Box display="flex" flexDirection="column" width="100%">
                      {items.map((item, index) => {
                        const itemId = item?.id ?? `${topic}-${index}`;
                        const isItemExpanded = expandedItemId === itemId;

                        return (
                          <Accordion
                            className={classes.innerAccordion}
                            elevation={0}
                            expanded={isItemExpanded}
                            key={itemId}
                            onChange={handleItemChange(itemId)}
                          >
                            <AccordionSummary
                              className={classes.innerAccordionSummary}
                              expandIcon={<ExpandMoreIcon />}
                            >
                              <Typography variant={isItemExpanded ? 'subtitle1' : 'body1'}>
                                {item?.title}
                              </Typography>
                            </AccordionSummary>

                            <AccordionDetails className={classes.innerAccordionDetails}>
                              <Typography
                                className={classes.assistantTypography}
                                component="div"
                                dangerouslySetInnerHTML={{ __html: item?.verbiage || '' }}
                                variant="body2"
                              />
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </Box>
    </StatusManager>
  );
};
